import { useState } from 'react';
import { Check, Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface OrderProduct {
  id: string;
  name: string;
  fullCode: string;
}

export interface OrderProductResult extends OrderProduct {
  sku?: string | null;
  model: { name: string; brand: { name: string } };
  images: { url: string }[];
  availableQty: number;
  stockStatus: string;
}

export function OrderProductIdentity({
  product,
  quantity,
}: {
  product: OrderProductResult;
  quantity?: number;
}) {
  const { t, i18n } = useTranslation('trading');
  const [failedImage, setFailedImage] = useState<string | null>(null);
  const imageUrl = product.images[0]?.url;
  const available = product.stockStatus === 'IN_STOCK' || product.stockStatus === 'LOW_STOCK';
  const stockLabel = available
    ? t('sales.orders.availableQuantity', {
        quantity: new Intl.NumberFormat(i18n.language).format(product.availableQty),
      })
    : t(
        product.stockStatus === 'PRE_ORDER'
          ? 'products.preOrder'
          : product.stockStatus === 'DISCONTINUED'
            ? 'products.discontinued'
            : 'products.outOfStock'
      );

  return (
    <>
      <span className="bg-muted/30 relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden border">
        {imageUrl && imageUrl !== failedImage ? (
          <img
            src={imageUrl}
            alt=""
            loading="lazy"
            className="h-full w-full object-contain"
            onError={() => setFailedImage(imageUrl)}
          />
        ) : (
          <Package className="text-muted-foreground h-5 w-5" />
        )}
        {!!quantity && (
          <span className="bg-primary text-primary-foreground absolute bottom-0 right-0 p-0.5">
            <Check className="h-3 w-3" />
          </span>
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="text-muted-foreground block text-xs">
          {product.model.brand.name} · {product.model.name}
        </span>
        <span className="mt-0.5 block break-words text-sm font-medium">{product.name}</span>
        <span className="text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs">
          <span className="break-all font-mono">{product.fullCode}</span>
          {product.sku && <span className="break-all font-mono">SKU: {product.sku}</span>}
          <span className={available ? 'text-emerald-700 dark:text-emerald-400' : ''}>
            {stockLabel}
          </span>
        </span>
        {!!quantity && (
          <span className="mt-1 block text-xs font-medium">
            {t('sales.orders.quantityInOrder', { quantity })}
          </span>
        )}
      </span>
    </>
  );
}
